import { useForm, zodResolver } from "@mantine/form";

import {
  LABOR_QUESTIONNAIRE_DISPLAY_NAMES,
  QuestionnaireType,
} from "@/encounters/components/EncounterLabor/constants/labor-questionnaires";
import { LaborStageSelectField } from "@/encounters/components/EncounterLabor/EncounterLaborAmend/LaborStageSelectField";
import { InputOutputFormFields } from "@/encounters/components/EncounterLabor/flowsheet-forms/input-output/components/InputOutputFormFields";
import {
  INPUT_OUTPUT_DATE_LINK_ID,
  INPUT_OUTPUT_DATE_TIME_LINK_ID,
  INPUT_OUTPUT_QUESTIONNAIRE_NAME,
  INPUT_OUTPUT_ROOT_LINK_ID,
  INPUT_OUTPUT_TIME_LINK_ID,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/input-output/constants/input-output-link-ids";
import type { InputOutputFlowsheetDrawerProps } from "@/encounters/components/EncounterLabor/flowsheet-forms/input-output/types/input-output-drawer";
import type { InputOutputFlowsheetFormValues } from "@/encounters/components/EncounterLabor/flowsheet-forms/input-output/types/input-output-form";
import {
  getDefaultInputOutputFlowsheetFormValues,
  normalizeInputOutputFlowsheetFormValues,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/input-output/utils/input-output-form-defaults";
import {
  mapInputOutputFormValuesToQuestionnaireValues,
  mapQuestionnaireResponseToInputOutputFormValues,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/input-output/utils/input-output-questionnaire-mapping";
import { inputOutputFlowsheetFormSchema } from "@/encounters/components/EncounterLabor/flowsheet-forms/input-output/zod-schemas/input-output-flowsheet-form";
import {
  LABOR_FLOWSHEET_DRAWER_MODES,
  LABOR_FLOWSHEET_SUBMIT_BUTTON_TEXT,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/labor-flowsheet-drawer-constants";
import { LaborFlowsheetDrawerShell } from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/LaborFlowsheetDrawerShell";
import {
  type LaborFlowsheetDrawerEditContext,
  useLaborFlowsheetDrawerPersist,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/useLaborFlowsheetDrawerPersist";

const INPUT_OUTPUT_DISPLAY_NAME = LABOR_QUESTIONNAIRE_DISPLAY_NAMES[QuestionnaireType.InputOutput];

function getInputOutputEditContext(
  props: InputOutputFlowsheetDrawerProps,
): LaborFlowsheetDrawerEditContext | undefined {
  if (props.mode !== LABOR_FLOWSHEET_DRAWER_MODES.Edit) {
    return undefined;
  }

  return {
    observation: props.observation,
    questionnaireDisplayName: props.questionnaireDisplayName,
  };
}

function mergeInputOutputHydratedEditFormValues({
  mapped,
  hydratedQuestionnaireValues,
}: {
  mapped: InputOutputFlowsheetFormValues;
  hydratedQuestionnaireValues: Record<string, unknown>;
}): InputOutputFlowsheetFormValues {
  const hydratedRoot = hydratedQuestionnaireValues[INPUT_OUTPUT_ROOT_LINK_ID] as
    | Record<string, unknown>
    | undefined;
  const hydratedDateTime = hydratedRoot?.[INPUT_OUTPUT_DATE_TIME_LINK_ID] as
    | Record<string, unknown>
    | undefined;
  const hydratedInputOutputDate = hydratedDateTime?.[INPUT_OUTPUT_DATE_LINK_ID];
  const hydratedInputOutputTime = hydratedDateTime?.[INPUT_OUTPUT_TIME_LINK_ID];

  return {
    ...mapped,
    inputOutputDate:
      hydratedInputOutputDate instanceof Date ? hydratedInputOutputDate : mapped.inputOutputDate,
    inputOutputTime:
      typeof hydratedInputOutputTime === "string"
        ? hydratedInputOutputTime
        : mapped.inputOutputTime,
  };
}

export function InputOutputFlowsheetDrawer(props: InputOutputFlowsheetDrawerProps) {
  const { opened, onClose, clientId, encounterId, mode } = props;

  const form = useForm<InputOutputFlowsheetFormValues>({
    initialValues: getDefaultInputOutputFlowsheetFormValues(),
    validate: zodResolver(inputOutputFlowsheetFormSchema),
  });

  const {
    isLoading,
    isSubmitting,
    loadError,
    dateTimeUtcDateAnchors,
    drawerLaborStageLoincCode,
    setDrawerLaborStageLoincCode,
    showLaborStageInDrawer,
    drawerTitle,
    handleSubmit,
  } = useLaborFlowsheetDrawerPersist<InputOutputFlowsheetFormValues>({
    opened,
    mode,
    onClose,
    clientId,
    encounterId,
    questionnaireName: INPUT_OUTPUT_QUESTIONNAIRE_NAME,
    displayName: INPUT_OUTPUT_DISPLAY_NAME,
    timerKey: "inputOutput",
    form,
    parseAndNormalizeFormValues: (values) =>
      normalizeInputOutputFlowsheetFormValues(inputOutputFlowsheetFormSchema.parse(values)),
    getDefaultFormValues: getDefaultInputOutputFlowsheetFormValues,
    mapFormValuesToQuestionnaireValues: mapInputOutputFormValuesToQuestionnaireValues,
    mapQuestionnaireResponseToFormValues: mapQuestionnaireResponseToInputOutputFormValues,
    mergeHydratedEditFormValues: mergeInputOutputHydratedEditFormValues,
    questionnaireNotFoundMessage: "Input/Output questionnaire not found.",
    editEntryNotFoundMessage: "Could not load existing input/output entry.",
    editContext: getInputOutputEditContext(props),
    onRefresh: props.mode === LABOR_FLOWSHEET_DRAWER_MODES.QuickAdd ? props.onRefresh : undefined,
    title: props.title,
  });

  return (
    <LaborFlowsheetDrawerShell
      opened={opened}
      onClose={onClose}
      error={loadError}
      title={drawerTitle}
      isLoading={isLoading}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      dateTimeUtcDateAnchors={dateTimeUtcDateAnchors}
      formId={`labor-flowsheet-form-${QuestionnaireType.InputOutput}`}
      submitButtonText={
        mode === LABOR_FLOWSHEET_DRAWER_MODES.Edit
          ? LABOR_FLOWSHEET_SUBMIT_BUTTON_TEXT.Update
          : LABOR_FLOWSHEET_SUBMIT_BUTTON_TEXT.AddToFlowsheet
      }
      headerSection={
        showLaborStageInDrawer && drawerLaborStageLoincCode ? (
          <LaborStageSelectField
            value={drawerLaborStageLoincCode}
            onChange={setDrawerLaborStageLoincCode}
          />
        ) : null
      }
    >
      <InputOutputFormFields form={form} mode={mode} clientId={clientId} />
    </LaborFlowsheetDrawerShell>
  );
}
